import React, { useState, useEffect } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { useToast } from '../Toast';
import Icon from '../Icon';
import { C, S } from '../../utils/styles';
import StyledSelect from '../StyledSelect';

const UNIT_OPTIONS = [
  { value: 'month', label: 'Month', labelPlural: 'Months' },
  { value: 'year', label: 'Year', labelPlural: 'Years' },
  { value: 'visit', label: 'Visit', labelPlural: 'Visits' },
  { value: 'day', label: 'Day', labelPlural: 'Days' },
  { value: 'trip', label: 'Trip', labelPlural: 'Trips' },
];

export default function PlanTiersEditor() {
  const { config, updateConfig } = useConfig();
  const addToast = useToast();
  const [draft, setDraft] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'archived'
  const [showNewPlanModal, setShowNewPlanModal] = useState(false);
  const [expandedPlans, setExpandedPlans] = useState({});

  useEffect(() => {
    if (config.planTiers) {
      const clonedData = JSON.parse(JSON.stringify(config.planTiers));
      setDraft(clonedData);
      // Expand all active plans by default
      const expanded = {};
      clonedData.filter(p => !p.archived).forEach(p => {
        expanded[p.id] = true;
      });
      setExpandedPlans(expanded);
    }
  }, [config.planTiers]);

  const activePlans = draft.filter(p => !p.archived);
  const archivedPlans = draft.filter(p => p.archived);
  const displayedPlans = activeTab === 'active' ? activePlans : archivedPlans;

  const updatePlanField = (planId, field, value) => {
    const newDraft = draft.map(p => {
      if (p.id === planId) {
        const updatedPlan = { ...p, [field]: value };
        // Auto-calculate spread when premium or claim changes
        if (field === 'monthlyPremium' || field === 'monthlyClaim') {
          const premium = field === 'monthlyPremium' ? value : p.monthlyPremium;
          const claim = field === 'monthlyClaim' ? value : p.monthlyClaim;
          updatedPlan.spread = premium - claim;
        }
        return updatedPlan;
      }
      return p;
    });
    setDraft(newDraft);
    setHasChanges(true);
  };

  const updateBenefit = (planId, benIdx, field, value) => {
    const newDraft = draft.map(p => {
      if (p.id === planId) {
        const newBenefits = [...p.benefits];
        newBenefits[benIdx] = { ...newBenefits[benIdx], [field]: value };
        return { ...p, benefits: newBenefits };
      }
      return p;
    });
    setDraft(newDraft);
    setHasChanges(true);
  };

  const togglePlanExpansion = (planId) => {
    setExpandedPlans(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  const handleArchive = async (planId) => {
    const plan = draft.find(p => p.id === planId);
    if (!plan) return;

    if (window.confirm(`Are you sure you want to archive "${plan.label}"? It will no longer be active in routines.`)) {
      const newDraft = draft.map(p => p.id === planId ? { ...p, archived: true } : p);
      setDraft(newDraft);
      
      // Auto-save the change
      try {
        await updateConfig({ planTiers: newDraft });
        addToast(`${plan.label} archived successfully`, 'success');
        setHasChanges(false);
      } catch (err) {
        addToast('Failed to archive plan', 'error');
        // Revert on error
        setDraft(draft);
      }
    }
  };

  const handleUnarchive = async (planId) => {
    const plan = draft.find(p => p.id === planId);
    if (!plan) return;

    const newDraft = draft.map(p => p.id === planId ? { ...p, archived: false } : p);
    setDraft(newDraft);
    setExpandedPlans(prev => ({ ...prev, [planId]: true }));
    
    // Auto-save the change
    try {
      await updateConfig({ planTiers: newDraft });
      addToast(`${plan.label} restored to active plans`, 'success');
      setHasChanges(false);
    } catch (err) {
      addToast('Failed to restore plan', 'error');
      // Revert on error
      setDraft(draft);
    }
  };

  const handleAddNewPlan = (newPlan) => {
    const newDraft = [...draft, newPlan];
    setDraft(newDraft);
    setExpandedPlans(prev => ({ ...prev, [newPlan.id]: true }));
    setHasChanges(true);
    setShowNewPlanModal(false);
    addToast(`${newPlan.label} created successfully`, 'success');
  };

  const handleSave = async () => {
    try {
      await updateConfig({ planTiers: draft });
      addToast('Plan tiers updated successfully', 'success');
      setHasChanges(false);
    } catch (err) {
      addToast('Failed to save changes', 'error');
    }
  };

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={S.card}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h3 style={{ ...S.heading, margin: 0 }}>Plan Tier Management</h3>
            <p style={{ fontSize: 12, color: C.textMid, marginTop: 4 }}>
              Configure benefit amounts, claim payments, and manage plan lifecycle
            </p>
          </div>
          <button
            data-testid="add-new-plan-button"
            onClick={() => setShowNewPlanModal(true)}
            style={{
              ...S.buttonPrimary,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <Icon name="add-outline" style={{ fontSize: 18 }} />
            Add New Plan
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: 8,
          borderBottom: `1px solid ${C.border}`,
          marginBottom: 20
        }}>
          <button
            data-testid="active-plans-tab"
            onClick={() => setActiveTab('active')}
            style={{
              padding: '10px 16px',
              border: 'none',
              borderBottom: activeTab === 'active' ? `2px solid ${C.accent}` : '2px solid transparent',
              background: 'transparent',
              color: activeTab === 'active' ? C.accent : C.textMid,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: "'Inter', sans-serif"
            }}
          >
            Active Plans ({activePlans.length})
          </button>
          <button
            data-testid="archived-plans-tab"
            onClick={() => setActiveTab('archived')}
            style={{
              padding: '10px 16px',
              border: 'none',
              borderBottom: activeTab === 'archived' ? `2px solid ${C.accent}` : '2px solid transparent',
              background: 'transparent',
              color: activeTab === 'archived' ? C.accent : C.textMid,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: "'Inter', sans-serif"
            }}
          >
            Archived Plans ({archivedPlans.length})
          </button>
        </div>

        {/* Plans List */}
        {displayedPlans.length === 0 ? (
          <div style={{
            padding: 40,
            textAlign: 'center',
            background: 'rgb(248, 250, 252)',
            borderRadius: 10,
            border: `1px solid ${C.border}`
          }}>
            <Icon name="folder-open-outline" style={{ fontSize: 48, color: C.textMid }} />
            <p style={{ marginTop: 12, color: C.textMid, fontSize: 14 }}>
              {activeTab === 'active' ? 'No active plans' : 'No archived plans'}
            </p>
          </div>
        ) : (
          displayedPlans.map((plan) => (
            <div
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              style={{
                marginBottom: 16,
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                overflow: 'hidden'
              }}
            >
              {/* Plan Header */}
              <div
                onClick={() => togglePlanExpansion(plan.id)}
                style={{
                  padding: 16,
                  background: expandedPlans[plan.id] ? 'white' : 'rgb(248, 250, 252)',
                  borderBottom: expandedPlans[plan.id] ? `1px solid ${C.border}` : 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Icon
                    name={expandedPlans[plan.id] ? 'chevron-down-outline' : 'chevron-forward-outline'}
                    style={{ fontSize: 20, color: C.textMid }}
                  />
                  <div>
                    <h4 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: 0 }}>
                      {plan.label}
                    </h4>
                    <div style={{ fontSize: 12, color: C.textMid, marginTop: 4, display: 'flex', gap: 12 }}>
                      <span>Premium: ${plan.monthlyPremium || 0}/mo</span>
                      <span>•</span>
                      <span>Claim Payment: ${plan.monthlyClaim || 0}/mo</span>
                      <span>•</span>
                      <span>Spread: ${plan.spread || 0}</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {activeTab === 'active' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleArchive(plan.id);
                      }}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 8,
                        border: 'none',
                        background: 'rgb(226, 232, 240)',
                        color: 'rgb(71, 85, 105)',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontFamily: "'Inter', sans-serif",
                        transition: 'all 0.15s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = 'rgb(203, 213, 225)'}
                      onMouseLeave={(e) => e.target.style.background = 'rgb(226, 232, 240)'}
                    >
                      <Icon name="archive-outline" style={{ fontSize: 16 }} />
                      Archive
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUnarchive(plan.id);
                      }}
                      style={{
                        ...S.buttonPrimary,
                        padding: '6px 12px',
                        fontSize: 12
                      }}
                    >
                      <Icon name="arrow-undo-outline" style={{ fontSize: 16, marginRight: 4 }} />
                      Restore
                    </button>
                  )}
                </div>
              </div>

              {/* Plan Details */}
              {expandedPlans[plan.id] && (
                <div style={{ padding: 20, background: 'white' }}>
                  {/* Plan Settings Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 16,
                    marginBottom: 20,
                    padding: 16,
                    background: 'rgb(248, 250, 252)',
                    borderRadius: 8
                  }}>
                    <div>
                      <label htmlFor={`premium-${plan.id}`} style={S.label}>Monthly Premium</label>
                      <input
                        id={`premium-${plan.id}`}
                        name={`premium-${plan.id}`}
                        type="number"
                        value={plan.monthlyPremium || 0}
                        onChange={(e) => updatePlanField(plan.id, 'monthlyPremium', +e.target.value)}
                        style={S.input}
                      />
                    </div>
                    <div>
                      <label htmlFor={`claim-${plan.id}`} style={S.label}>Monthly Claim Payment</label>
                      <input
                        id={`claim-${plan.id}`}
                        name={`claim-${plan.id}`}
                        data-testid={`claim-payment-${plan.id}`}
                        type="number"
                        value={plan.monthlyClaim || 0}
                        onChange={(e) => updatePlanField(plan.id, 'monthlyClaim', +e.target.value)}
                        style={S.input}
                      />
                    </div>
                    <div>
                      <label htmlFor={`spread-${plan.id}`} style={S.label}>Spread (Auto-calculated)</label>
                      <input
                        id={`spread-${plan.id}`}
                        name={`spread-${plan.id}`}
                        type="number"
                        value={plan.spread || 0}
                        readOnly
                        disabled
                        style={{
                          ...S.input,
                          background: 'rgb(241, 245, 249)',
                          cursor: 'not-allowed',
                          color: C.textMid
                        }}
                      />
                    </div>
                  </div>

                  {/* Benefits Table */}
                  <h5 style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 12 }}>
                    Benefits Configuration
                  </h5>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'rgb(248, 250, 252)', borderBottom: `1px solid ${C.border}` }}>
                        <th style={{ ...S.th, textAlign: 'left' }}>Benefit Type</th>
                        <th style={{ ...S.th, textAlign: 'left' }}>Fee Amount</th>
                        <th style={{ ...S.th, textAlign: 'left' }}>Quantity</th>
                        <th style={{ ...S.th, textAlign: 'left' }}>Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.benefits.map((ben, benIdx) => (
                        <tr key={benIdx} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={S.td}>{ben.name || ben.type}</td>
                          <td style={S.td}>
                            <input
                              type="number"
                              value={ben.fee || ben.amount || 0}
                              onChange={(e) => updateBenefit(plan.id, benIdx, 'fee', +e.target.value)}
                              style={{ ...S.input, fontSize: 12, padding: '6px 10px', width: 100 }}
                            />
                          </td>
                          <td style={S.td}>
                            <input
                              type="number"
                              value={ben.quantity || 0}
                              onChange={(e) => updateBenefit(plan.id, benIdx, 'quantity', +e.target.value)}
                              style={{ ...S.input, fontSize: 12, padding: '6px 10px', width: 80 }}
                            />
                          </td>
                          <td style={S.td}>
                            <StyledSelect
                              value={ben.unit}
                              onValueChange={(val) => updateBenefit(plan.id, benIdx, 'unit', val)}
                              options={UNIT_OPTIONS.map((u, idx) => ({ key: idx, value: u.value, label: u.labelPlural }))}
                              style={{ fontSize: 12, width: 120 }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))
        )}

        {/* Save Button */}
        {hasChanges && (
          <div style={{
            marginTop: 20,
            padding: 16,
            background: 'rgb(254, 243, 199)',
            border: '1px solid rgb(217, 119, 6)',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ fontSize: 13, color: 'rgb(120, 53, 15)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="warning" style={{ fontSize: 16 }} />
              You have unsaved changes
            </div>
            <button
              data-testid="save-plan-changes-button"
              onClick={handleSave}
              style={S.buttonPrimary}
            >
              Save All Changes
            </button>
          </div>
        )}
      </div>

      {/* New Plan Modal */}
      {showNewPlanModal && (
        <NewPlanModal
          existingPlans={draft}
          onClose={() => setShowNewPlanModal(false)}
          onAdd={handleAddNewPlan}
        />
      )}
    </div>
  );
}

// New Plan Modal Component
function NewPlanModal({ existingPlans, onClose, onAdd }) {
  const [newPlan, setNewPlan] = useState({
    id: '',
    label: '',
    monthlyPremium: 0,
    monthlyClaim: 0,
    spread: 0,
    benefits: [],
    archived: false
  });
  const [copyFromPlanId, setCopyFromPlanId] = useState('');

  // Auto-calculate spread when premium or claim changes
  const updatePlanValue = (field, value) => {
    const updated = { ...newPlan, [field]: value };
    if (field === 'monthlyPremium' || field === 'monthlyClaim') {
      updated.spread = updated.monthlyPremium - updated.monthlyClaim;
    }
    setNewPlan(updated);
  };

  const handleCopyFromPlan = (planId) => {
    const sourcePlan = existingPlans.find(p => p.id === planId);
    if (sourcePlan) {
      setNewPlan(prev => ({
        ...prev,
        monthlyPremium: sourcePlan.monthlyPremium,
        monthlyClaim: sourcePlan.monthlyClaim,
        spread: sourcePlan.monthlyPremium - sourcePlan.monthlyClaim,
        benefits: JSON.parse(JSON.stringify(sourcePlan.benefits))
      }));
    }
  };

  const handleAdd = () => {
    if (!newPlan.id || !newPlan.label) {
      alert('Please enter Plan ID and Label');
      return;
    }

    if (existingPlans.some(p => p.id === newPlan.id)) {
      alert('Plan ID already exists');
      return;
    }

    onAdd(newPlan);
  };

  return (
    <div
      data-testid="new-plan-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'white',
        borderRadius: 14,
        boxShadow: '0 10px 30px rgba(2, 6, 23, 0.15)',
        maxWidth: 600,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: 24
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ ...S.heading, margin: 0 }}>Add New Plan</h3>
          <button
            onClick={onClose}
            style={{
              padding: 8,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: C.textMid
            }}
          >
            <Icon name="close-outline" style={{ fontSize: 24 }} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label htmlFor="new-plan-id" style={S.label}>Plan ID *</label>
            <input
              id="new-plan-id"
              name="newPlanId"
              type="text"
              placeholder="e.g., 2000"
              value={newPlan.id}
              onChange={(e) => updatePlanValue('id', e.target.value)}
              style={S.input}
            />
          </div>

          <div>
            <label htmlFor="new-plan-label" style={S.label}>Plan Label *</label>
            <input
              id="new-plan-label"
              name="newPlanLabel"
              type="text"
              placeholder="e.g., Plan 2000"
              value={newPlan.label}
              onChange={(e) => updatePlanValue('label', e.target.value)}
              style={S.input}
            />
          </div>

          <div>
            <label htmlFor="copy-from-plan" style={S.label}>Copy Benefits From Existing Plan (Optional)</label>
            <StyledSelect
              value={copyFromPlanId}
              onValueChange={(val) => {
                setCopyFromPlanId(val);
                if (val) handleCopyFromPlan(val);
              }}
              options={[
                { key: 'none', value: '', label: 'Start from scratch' },
                ...existingPlans.filter(p => !p.archived).map((p, idx) => ({
                  key: idx + 1,
                  value: p.id,
                  label: p.label
                }))
              ]}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label htmlFor="new-plan-premium" style={S.label}>Monthly Premium</label>
              <input
                id="new-plan-premium"
                name="newPlanPremium"
                type="number"
                value={newPlan.monthlyPremium}
                onChange={(e) => updatePlanValue('monthlyPremium', +e.target.value)}
                style={S.input}
              />
            </div>
            <div>
              <label htmlFor="new-plan-claim" style={S.label}>Monthly Claim</label>
              <input
                id="new-plan-claim"
                name="newPlanClaim"
                type="number"
                value={newPlan.monthlyClaim}
                onChange={(e) => updatePlanValue('monthlyClaim', +e.target.value)}
                style={S.input}
              />
            </div>
            <div>
              <label htmlFor="new-plan-spread" style={S.label}>Spread (Auto-calculated)</label>
              <input
                id="new-plan-spread"
                name="newPlanSpread"
                type="number"
                value={newPlan.spread}
                readOnly
                disabled
                style={{
                  ...S.input,
                  background: 'rgb(241, 245, 249)',
                  cursor: 'not-allowed',
                  color: C.textMid
                }}
              />
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
            marginTop: 16
          }}>
            <button
              onClick={onClose}
              style={S.buttonSecondary}
            >
              Cancel
            </button>
            <button
              data-testid="create-plan-button"
              onClick={handleAdd}
              style={S.buttonPrimary}
            >
              Create Plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
